import cn from 'classnames';
import { sanitize } from 'isomorphic-dompurify';

import { SearchPageActiveFilter } from 'src/hooks/useSearchPage';

import CloseIcon from 'assets/icons/close.svg';

import Text from '../Text';

import styles from './ActiveFiltersList.module.scss';

export type ActiveFiltersListProps<TFilterName extends string> = {
  labels: Record<TFilterName | 'query' | 'clear-all', string>;
  activeFilters: Array<SearchPageActiveFilter<TFilterName>>;
  clearAll: () => void;
  sortFilter?: {
    sectionLabel: string;
    sortLabel: string;
    clearSort: () => void;
  };
};

const ActiveFiltersList = <TFilterName extends string = string>(
  props: ActiveFiltersListProps<TFilterName>
): JSX.Element => {
  const { activeFilters, clearAll, labels, sortFilter } = props;

  const filtersGroupedByFilterName = activeFilters.reduce(
    (acc, activeFilter) => {
      if (activeFilter.type === 'query') {
        acc.query.push(activeFilter);
      } else {
        if (!acc.filters[activeFilter.filterName]) {
          acc.filters[activeFilter.filterName] = [];
        }

        acc.filters[activeFilter.filterName].push(activeFilter);
      }

      return acc;
    },
    {
      query: [] as SearchPageActiveFilter<TFilterName>[],
      filters: {} as Record<
        TFilterName,
        Extract<SearchPageActiveFilter<TFilterName>, { type: 'filter' }>[]
      >,
    }
  );

  return (
    <div className={styles.main}>
      {filtersGroupedByFilterName.query.length > 0 && (
        <div className={styles.group}>
          <div className={cn(styles['group-title'], 'body-eyebrow')}>{labels.query}</div>
          <div className={styles['buttons']}>
            {filtersGroupedByFilterName.query.map((activeFilter) => (
              <button
                className={cn(styles.pill, 'body-sm')}
                key={`query-${activeFilter.value}`}
                onClick={activeFilter.clear}
                type="button"
              >
                <Text field={sanitize(activeFilter.label)} />
                <CloseIcon aria-hidden="true" className={styles['pill-close']} />
              </button>
            ))}
          </div>
        </div>
      )}

      {Object.keys(filtersGroupedByFilterName.filters).map((filterName) => {
        const filterGroup = filtersGroupedByFilterName.filters[filterName as TFilterName];
        return (
          <div className={styles.group} key={filterName}>
            <Text
              className={cn(styles['group-title'], 'body-eyebrow')}
              field={`
                ${labels[filterName as TFilterName]}
                ${filterGroup.length > 1 ? ` (${filterGroup.length})` : ''}
              `}
            />
            <div className={styles['buttons']}>
              {filterGroup.map((activeFilter) => (
                <button
                  className={cn(styles.pill, 'body-sm')}
                  key={`${activeFilter.filterName}-${activeFilter.value}`}
                  onClick={activeFilter.clear}
                  type="button"
                >
                  <Text field={sanitize(activeFilter.label)} />
                  <CloseIcon aria-hidden="true" className={styles['pill-close']} />
                </button>
              ))}
            </div>
          </div>
        );
      })}
      {sortFilter && (
        <div className={styles.group} key="sortFilter">
          <div className={cn(styles['group-title'], 'body-eyebrow')}>{sortFilter.sectionLabel}</div>
          <div className={styles['buttons']}>
            <button
              className={cn(styles.pill, 'body-sm')}
              onClick={sortFilter.clearSort}
              type="button"
            >
              <Text field={sortFilter.sortLabel} />
              <CloseIcon aria-hidden="true" className={styles['pill-close']} />
            </button>
          </div>
        </div>
      )}
      {(activeFilters.length >= 2 || (sortFilter && activeFilters.length >= 1)) && (
        <div className={styles['clear-all']}>
          <button
            className={cn(styles.pill, styles['clear-all-pill'], 'body-eyebrow')}
            onClick={() => {
              sortFilter?.clearSort();
              clearAll();
            }}
            type="button"
          >
            <Text field={labels['clear-all']} />
            <CloseIcon aria-hidden="true" className={styles['pill-close']} />
          </button>
        </div>
      )}
    </div>
  );
};

export default ActiveFiltersList;
